import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  FiGrid,
  FiUsers,
  FiTruck,
  FiUserCheck,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiSun,
  FiMoon,
  FiX,
  FiClipboard,
  FiTool,
  FiBell,
  FiCalendar,
  FiSettings,
  FiFileText,
  FiActivity,
  FiPackage,
  FiBox,
  FiHome,
  FiCheckSquare,
  FiCheckCircle,
  FiStar,
  FiSend,
  FiRadio,
} from 'react-icons/fi';
import { useState, useEffect, useRef } from 'react';
import { useAuthStore, useThemeStore, useSettingsStore, useShopStore } from '../../store';
import { dashboardService, reminderService } from '../../services';
import ShopSwitcher from '../ShopSwitcher/ShopSwitcher';
import './Sidebar.css';

function Sidebar({ isOpen, onClose, collapsed = false, onCollapsedChange }) {
  const setCollapsed = (val) => onCollapsedChange?.(typeof val === 'function' ? val(collapsed) : val);
  const [alertsCount, setAlertsCount] = useState(0);
  const [remindersCount, setRemindersCount] = useState(0);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const sidebarRef = useRef(null);
  const { user, isAuthenticated, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const { settings } = useSettingsStore();
  const vehiclesLabel = (settings?.shopType === 'motorcycles') ? 'Motos' : 'Vehículos';
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch alerts count
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchAlertsCount = async () => {
      try {
        const response = await dashboardService.getAlertsCount();
        setAlertsCount(response.data.count || 0);
      } catch (err) {
        console.error('Error fetching alerts count:', err);
      }
    };

    fetchAlertsCount();
    const interval = setInterval(fetchAlertsCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Fetch reminders count
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!['admin', 'superadmin'].includes(user?.role)) return;

    const fetchRemindersCount = async () => {
      try {
        const response = await reminderService.getRemindersCount();
        const countData = response.data?.data || response.data;
        setRemindersCount(countData?.overdue || 0);
      } catch (err) {
        console.error('Error fetching reminders count:', err);
      }
    };

    fetchRemindersCount();
    const interval = setInterval(fetchRemindersCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user?.role]);

  // Admin menu structure with groups
  const adminMenuStructure = [
    { path: '/dashboard', icon: FiGrid, label: 'Panel' },
    {
      id: 'clientes',
      label: 'Clientes',
      icon: FiUsers,
      children: [
        { path: '/customers', icon: FiUsers, label: 'Clientes' },
        { path: '/vehicles', icon: FiTruck, label: vehiclesLabel },
        { path: '/ratings', icon: FiStar, label: 'Valoraciones' },
      ],
    },
    {
      id: 'operaciones',
      label: 'Operaciones',
      icon: FiClipboard,
      children: [
        { path: '/workorders', icon: FiClipboard, label: 'Órdenes', badge: alertsCount },
        { path: '/quotes', icon: FiFileText, label: 'Cotizaciones' },
        { path: '/calendar', icon: FiCalendar, label: 'Calendario' },
        { path: '/workload', icon: FiActivity, label: 'Carga de Trabajo' },
        { path: '/reminders', icon: FiBell, label: 'Recordatorios', badge: remindersCount },
        { path: '/follow-ups', icon: FiCheckCircle, label: 'Seguimiento' },
        { path: '/shipments', icon: FiSend, label: 'Envíos' },
      ],
    },
    {
      id: 'catalogo',
      label: 'Catálogo',
      icon: FiTool,
      children: [
        { path: '/services', icon: FiTool, label: 'Servicios' },
        { path: '/inspections', icon: FiCheckSquare, label: 'Inspecciones' },
        { path: '/inventory', icon: FiBox, label: 'Inventario' },
        { path: '/parts-report', icon: FiPackage, label: 'Repuestos' },
      ],
    },
  ];

  // Auto-expand group matching current route (for mobile accordion)
  useEffect(() => {
    const currentPath = location.pathname;
    for (const item of adminMenuStructure) {
      if (item.children) {
        const match = item.children.some((child) => currentPath.startsWith(child.path));
        if (match) {
          setExpandedGroup(item.id);
          return;
        }
      }
    }
  }, [location.pathname]);

  // No mostrar sidebar si no está autenticado
  if (!isAuthenticated) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose?.();
  };

  const handleNavClick = () => {
    onClose?.();
  };

  const toggleGroup = (id) => {
    setExpandedGroup((prev) => (prev === id ? null : id));
  };

  const handleGroupEnter = (e, groupId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const sidebarEl = sidebarRef.current;
    const left = sidebarEl ? sidebarEl.getBoundingClientRect().right : rect.right;
    setHoveredGroup(groupId);
    setFlyoutPos({ top: rect.top, left });
  };

  const handleGroupLeave = () => {
    setHoveredGroup(null);
  };

  // Filter hidden items from structure
  const filterStructure = (structure, hiddenItems) => {
    if (!hiddenItems.length) return structure;
    return structure
      .map((item) => {
        if (item.path) {
          return hiddenItems.includes(item.path) ? null : item;
        }
        if (item.children) {
          const filtered = item.children.filter((child) => !hiddenItems.includes(child.path));
          if (filtered.length === 0) return null;
          return { ...item, children: filtered };
        }
        return item;
      })
      .filter(Boolean);
  };

  // Sum badges from group children
  const getGroupBadge = (group) => {
    return group.children.reduce((sum, child) => sum + (child.badge || 0), 0);
  };

  // Check if any child in group is active
  const isGroupActive = (group) => {
    return group.children.some((child) => location.pathname.startsWith(child.path));
  };

  // Menu items for technician users (mechanics)
  const technicianMenuItems = [
    { path: '/workorders', icon: FiClipboard, label: 'Mis Órdenes' },
    { path: '/calendar', icon: FiCalendar, label: 'Calendario' },
    { path: '/workload', icon: FiActivity, label: 'Mi Carga de Trabajo' },
  ];

  const isAdminOrSuper = ['admin', 'superadmin'].includes(user?.role);
  const hiddenItems = settings?.hiddenMenuItems || [];

  const techMenuFiltered =
    hiddenItems.length > 0
      ? technicianMenuItems.filter((item) => !hiddenItems.includes(item.path))
      : technicianMenuItems;

  const menuStructure = isAdminOrSuper
    ? filterStructure(adminMenuStructure, hiddenItems)
    : null;

  const adminItems = [
    { path: '/users', icon: FiUserCheck, label: 'Usuarios' },
    { path: '/settings', icon: FiSettings, label: 'Configuración' },
  ];

  if (user?.role === 'superadmin') {
    adminItems.unshift({ path: '/online-users', icon: FiRadio, label: 'En Linea' });
    adminItems.unshift({ path: '/leads', icon: FiUsers, label: 'Leads' });
    adminItems.unshift({ path: '/shops', icon: FiHome, label: 'Talleres' });
  }

  // Render a standalone NavLink item
  const renderNavItem = (item) => (
    <li key={item.path}>
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          `nav-link-sidebar ${isActive ? 'active-sidebar' : ''}`
        }
        title={collapsed ? item.label : undefined}
        onClick={handleNavClick}
      >
        <span className="nav-icon-sidebar">
          <item.icon />
          {collapsed && item.badge > 0 && (
            <span className="nav-badge-collapsed-sidebar">{item.badge}</span>
          )}
        </span>
        {!collapsed && <span className="nav-text-sidebar">{item.label}</span>}
        {!collapsed && item.badge > 0 && (
          <span className="nav-badge-sidebar">{item.badge}</span>
        )}
        {!collapsed && <span className="nav-indicator-sidebar" />}
      </NavLink>
    </li>
  );

  // Render a group with hover flyout (desktop) / accordion (mobile)
  const renderGroup = (group) => {
    const groupActive = isGroupActive(group);
    const groupBadge = getGroupBadge(group);
    const isExpanded = expandedGroup === group.id;
    const isHovered = hoveredGroup === group.id;

    return (
      <li
        key={group.id}
        className={`nav-group-sidebar ${isExpanded ? 'group-expanded-sidebar' : ''}`}
        onMouseEnter={(e) => handleGroupEnter(e, group.id)}
        onMouseLeave={handleGroupLeave}
      >
        <div
          className={`nav-group-header-sidebar ${groupActive ? 'active-group-sidebar' : ''}`}
          onClick={() => toggleGroup(group.id)}
        >
          <span className="nav-icon-sidebar">
            <group.icon />
            {collapsed && groupBadge > 0 && (
              <span className="nav-badge-collapsed-sidebar">{groupBadge}</span>
            )}
          </span>
          {!collapsed && <span className="nav-text-sidebar">{group.label}</span>}
          {!collapsed && groupBadge > 0 && (
            <span className="nav-badge-sidebar">{groupBadge}</span>
          )}
          {!collapsed && (
            <span className={`nav-group-chevron-sidebar ${isExpanded ? 'expanded-sidebar' : ''}`}>
              <FiChevronRight />
            </span>
          )}
        </div>
        <ul
          className={`nav-group-flyout-sidebar ${isHovered ? 'visible-sidebar' : ''}`}
          style={{ top: flyoutPos.top, left: flyoutPos.left }}
        >
          <li className="nav-flyout-title-sidebar">{group.label}</li>
          {group.children.map((child) => (
            <li key={child.path}>
              <NavLink
                to={child.path}
                className={({ isActive }) =>
                  `nav-flyout-link-sidebar ${isActive ? 'active-sidebar' : ''}`
                }
                onClick={handleNavClick}
              >
                <span className="nav-flyout-icon-sidebar">
                  <child.icon />
                </span>
                <span>{child.label}</span>
                {child.badge > 0 && (
                  <span className="nav-badge-flyout-sidebar">{child.badge}</span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </li>
    );
  };

  return (
    <aside
      ref={sidebarRef}
      className={`sidebar-sidebar ${collapsed ? 'collapsed-sidebar' : ''} ${isOpen ? 'open-sidebar' : ''}`}
    >
      {/* Logo Section */}
      <div className="logo-section-sidebar">
        <div className="logo-wrapper-sidebar">
          {settings?.logo ? (
            <img src={settings.logo} alt={settings?.name || 'Logo'} className="logo-image-sidebar" />
          ) : (
            <div className="logo-icon-sidebar">
              <FiTruck />
            </div>
          )}
          {!collapsed && (
            <div className="logo-text-sidebar">
              <span className="logo-title-sidebar">{settings?.name || 'Sistema de Taller'}</span>
              <span className="logo-subtitle-sidebar">{settings?.slogan || 'Sistema de Gestión'}</span>
            </div>
          )}
        </div>
        {/* Close button for mobile */}
        <button
          className="close-btn-sidebar"
          onClick={onClose}
          title="Cerrar menú"
        >
          <FiX />
        </button>
        {/* Collapse button for desktop */}
        <button
          className="collapse-btn-sidebar"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Expandir' : 'Colapsar'}
        >
          {collapsed ? <FiChevronRight /> : <FiChevronLeft />}
        </button>
      </div>

      {/* Shop Switcher (superadmin only) */}
      {user?.role === 'superadmin' && <ShopSwitcher collapsed={collapsed} />}

      {/* Navigation */}
      <nav className="nav-sidebar">
        <div className="nav-section-sidebar">
          {!collapsed && <span className="nav-label-sidebar">Menú Principal</span>}
          <ul className="nav-list-sidebar">
            {isAdminOrSuper
              ? menuStructure.map((item) => {
                  if (item.path) return renderNavItem(item);
                  if (item.children) return renderGroup(item);
                  return null;
                })
              : techMenuFiltered.map((item) => renderNavItem(item))}
          </ul>
        </div>

        {isAdminOrSuper && (
          <div className="nav-section-sidebar">
            {!collapsed && <span className="nav-label-sidebar">Administración</span>}
            <ul className="nav-list-sidebar">
              {adminItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `nav-link-sidebar ${isActive ? 'active-sidebar' : ''}`
                    }
                    title={collapsed ? item.label : undefined}
                    onClick={handleNavClick}
                  >
                    <span className="nav-icon-sidebar">
                      <item.icon />
                    </span>
                    {!collapsed && <span className="nav-text-sidebar">{item.label}</span>}
                    {!collapsed && <span className="nav-indicator-sidebar" />}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      {/* Theme Toggle */}
      <div className="theme-toggle-section-sidebar">
        <button
          className={`theme-toggle-sidebar ${theme}`}
          onClick={toggleTheme}
          title={theme === 'light' ? 'Modo Oscuro' : 'Modo Claro'}
        >
          <span className="theme-toggle-track-sidebar">
            <span className="theme-toggle-thumb-sidebar">
              {theme === 'light' ? <FiSun /> : <FiMoon />}
            </span>
          </span>
          {!collapsed && (
            <span className="theme-toggle-label-sidebar">
              {theme === 'light' ? 'Modo Claro' : 'Modo Oscuro'}
            </span>
          )}
        </button>
      </div>

      {/* User Section */}
      {user && (
        <div className="user-section-sidebar">
          <div className="user-info-sidebar">
            <div className="user-avatar-sidebar">
              {user.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            {!collapsed && (
              <div className="user-details-sidebar">
                <span className="user-name-sidebar">{user.name}</span>
                <span className="user-role-sidebar">
                  {user.role === 'superadmin' ? 'Super Admin' : user.role === 'admin' ? 'Administrador' : 'Mecánico'}
                </span>
              </div>
            )}
          </div>
          <button
            className="logout-btn-sidebar"
            onClick={handleLogout}
            title="Cerrar sesión"
          >
            <FiLogOut />
          </button>
        </div>
      )}
    </aside>
  );
}

export default Sidebar;
